import Checkbox from "../../core/Checkbox";
import CommType from "../CommType";
import useMlsList from "../useMlsList";
import TextInput from "../../core/TextInput";
import { useFeatureDiscovery } from "src/hooks/useFeatureDiscovery";
import FeatureDiscoveryTooltip from "../../FeatureDiscoveryTooltip";
import { MODAL_CONTENT } from "src/constants/zIndexes";
import MultiSelectCombobox from "../../core/MultiSelectComboBox";
import {
  statusList,
  bathsList,
  bedsList,
  searchDateTypeFilters,
  squareFeet as squareFeetList,
  distressedLevel as distressedLevelList,
  preforeclosureStatusTypesList,
} from "../../../data/selectionData";
import SmartDateFilter from "./SmartDateFilter";
import SearchFilterLayout from "./SearchFilterLayout";
import MultipleSelect from "../../common/MultipleSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../shadcn/Select";

import {
  useGetCustomUploadsQuery,
  useGetCustomUploadsSourcesQuery,
} from "src/hooks/useCustomUploads";
import PriceRangeSlider from "src/components/common/PriceRangeSlider";
import TagSearch from "../TagSearch";
import PropertyTypeFilter, { PropertyTypeOption } from "src/components/common/PropertyTypeFilter";
import { useCallback, useEffect, useMemo } from "react";

interface SelectOption {
  id: string | number;
  label: string;
  value: string;
}

interface SearchFiltersProps {
  onComplete: () => void;
  showListingStatus?: boolean;
  showSearchDateType?: boolean;
  showMlsFilter?: boolean;
  showLookupNameFilter?: boolean;
  showLookupSourceFilter?: boolean;
  showIncludeActiveOnMLS?: boolean;
  showDistressedFilters?: boolean;
  showPreForeclosureStatus?: boolean;
  showPropertyTypeFilter?: boolean;
  suppressPriceFilters?: boolean;
  selectedListingStatuses: string[];
  setSelectedListingStatuses: (statuses: string[]) => void;
  distressedLevel: string[];
  setDistressedLevel: (levels: string[]) => void;
  preForeclosureStatus: string[];
  setPreForeclosureStatus: (status: string[]) => void;
  searchDateType: string;
  setSearchDateType: (type: string) => void;
  dateSearchType: string;
  setDateSearchType: (type: string) => void;
  fromDate: string;
  setFromDate: (date: string) => void;
  toDate: string;
  setToDate: (date: string) => void;
  setDateRange: (from: string, to: string, preset?: string) => void;
  mls: string;
  setMls: (mls: string) => void;
  numBeds: string;
  setNumBeds: (beds: string) => void;
  numBaths: string;
  setNumBaths: (baths: string) => void;
  minPrice: string;
  setMinPrice: (price: number) => void;
  maxPrice: string;
  setMaxPrice: (price: number) => void;
  setPriceRange: (minPrice: number, maxPrice: number) => void;
  commType: string;
  setCommType: (type: string) => void;
  sqFootage: string;
  setSqFootage: (sqft: string) => void;
  postalCode: string;
  setPostalCode: (code: string) => void;
  showMLSActives: boolean;
  setShowActives: (show: boolean) => void;
  tags: string;
  setTags: (tags: string) => void;
  isTagsAndSearch: boolean;
  setIsTagsAndSearch: (isAndSearch: boolean) => void;
  isHideDNCPhoneNumbers?: boolean;
  setIsHideDNCPhoneNumbers?: (hide: boolean) => void;
  propertyTypes?: string[];
  setPropertyTypes?: (types: string[]) => void;
  propertyTypeOptions?: readonly PropertyTypeOption[];
  resetFilters: () => void;
  lookupSources: SelectOption[];
  handleSelectLookupSource: (item: SelectOption) => void;
  handleRemoveLookupSource: (item: SelectOption) => void;
  lookupIds: SelectOption[];
  handleSelectLookupName: (item: SelectOption) => void;
  handleRemoveLookupName: (item: SelectOption) => void;
}

const SearchFilters = ({
  onComplete,
  showListingStatus = false,
  showSearchDateType = false,
  showMlsFilter = false,
  showLookupNameFilter = false,
  showLookupSourceFilter,
  showIncludeActiveOnMLS = false,
  showDistressedFilters = false,
  showPreForeclosureStatus = false,
  showPropertyTypeFilter = false,
  suppressPriceFilters = false,
  selectedListingStatuses,
  setSelectedListingStatuses,
  distressedLevel,
  setDistressedLevel,
  preForeclosureStatus,
  setPreForeclosureStatus,
  searchDateType,
  setSearchDateType,
  dateSearchType,
  setDateSearchType,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  setDateRange,
  mls,
  setMls,
  numBeds,
  setNumBeds,
  numBaths,
  setNumBaths,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  setPriceRange,
  commType,
  setCommType,
  sqFootage,
  setSqFootage,
  postalCode,
  setPostalCode,
  showMLSActives,
  setShowActives,
  tags,
  setTags,
  isTagsAndSearch,
  setIsTagsAndSearch,
  isHideDNCPhoneNumbers,
  setIsHideDNCPhoneNumbers,
  propertyTypes = [],
  setPropertyTypes,
  propertyTypeOptions,
  resetFilters,
  lookupSources,
  handleSelectLookupSource,
  handleRemoveLookupSource,
  lookupIds,
  handleSelectLookupName,
  handleRemoveLookupName,
}: SearchFiltersProps) => {
  const mlsList = useMlsList();
  const { data: lookups } = useGetCustomUploadsQuery();
  const { data: sources } = useGetCustomUploadsSourcesQuery();

  // Add handler for price range changes
  const handlePriceRangeChange = useCallback(
    (newMinPrice: number, newMaxPrice: number) => {
      // Use batched update to avoid race condition
      setPriceRange(newMinPrice, newMaxPrice);
    },
    [setPriceRange]
  );

  const features = useMemo(
    () => ["filter-by-import-name", "filter-by-import-source"],
    []
  );

  const { nextFeature, isLastFeature, shouldShowTooltip } =
    useFeatureDiscovery(features);

  const numBathsOptions = bathsList.map((option) => ({
    id: option.value,
    label: option.name,
    value: option.value,
  }));


  const preForeclosureStatusOptions = preforeclosureStatusTypesList.map(
    (option) => ({
      id: option.code,
      label: option.name,
      value: option.code,
    })
  );

  const selectedPreForeclosureStatusOptions = useMemo(
    () =>
      preForeclosureStatusOptions.filter(({ value }) =>
        preForeclosureStatus.includes(value)
      ),
    [preForeclosureStatusOptions, preForeclosureStatus]
  );

  const distressedLevelOptions = distressedLevelList.map((option) => ({
    id: option.value,
    label: option.name,
    value: option.value,
  }));

  const selectedDistressedLevelOptions = useMemo(
    () =>
      distressedLevelOptions.filter(({ value }) =>
        distressedLevel.includes(value)
      ),
    [distressedLevelOptions, distressedLevel]
  );

  const numBedsOptions = bedsList.map((option) => ({
    id: option.value,
    label: option.name,
    value: option.value,
  }));

  const sqFootOptions = squareFeetList.map((option) => ({
    id: option.value,
    label: option.name,
    value: option.value,
  }));


  const searchDateTypeOptions = searchDateTypeFilters.map((option) => ({
    id: option.value,
    label: option.name,
    value: option.value,
    short: option.short,
  }));


  const mlsOptions = useMemo(
    () =>
      mlsList.map((option) => ({
        id: option.customer_mls_id,
        label: option.name,
        value: `${option.customer_mls_id}`,
      })),
    [mlsList]
  );


  const lookupsNameOptions = useMemo(
    () =>
      (lookups?.imports || [])
        .filter(({ name }: { name: string }) => !!name)
        .map(({ name, id }: { name: string; id: string | number }) => ({
          id,
          label: name,
          value: id,
        })),
    [lookups]
  );

  const lookupSourceOptions = useMemo(
    () =>
      (sources || []).map(({ source }: { source: string }) => ({
        id: source,
        label: source,
        value: source,
      })),
    [sources]
  );

  const handleDistressedLevelChange = useCallback(
    (newValue: SelectOption[]) => {
      setDistressedLevel(newValue.map((item) => item.value));
    },
    [setDistressedLevel]
  );

  const handlePreForeclosureStatusChange = useCallback(
    (newValue: SelectOption[]) => {
      setPreForeclosureStatus(newValue.map((item) => item.value));
    },
    [setPreForeclosureStatus]
  );

  const handleShowMLSActivesChange = (newValue: boolean) => {
    setShowActives(newValue);
  };

  const handleHideDNCPhoneNumbersChange = (showDNC: boolean) => {
    if (setIsHideDNCPhoneNumbers) {
      // When checkbox is checked (showDNC=true), we want isHideDNCPhoneNumbers=false
      setIsHideDNCPhoneNumbers(!showDNC);
    }
  };

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        onComplete();
      }
    },
    [onComplete]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <div className="search-filters mb-10">
      <div className="flex flex-col gap-10">
        <SearchFilterLayout label="Date Range">
          <SmartDateFilter
            fromDate={fromDate}
            toDate={toDate}
            dateSearchType={dateSearchType}
            setFromDate={setFromDate}
            setToDate={setToDate}
            setDateRange={setDateRange}
            setDateSearchType={setDateSearchType}
            showSearchDateType={showSearchDateType}
            searchDateType={searchDateType}
            setSearchDateType={setSearchDateType}
            searchDateTypeOptions={searchDateTypeOptions}
          />
        </SearchFilterLayout>
        {!suppressPriceFilters && (
          <SearchFilterLayout label="Price">
            <PriceRangeSlider
              range={[
                minPrice ? Number(minPrice) : 0,
                maxPrice ? Number(maxPrice) : 0,
              ]}
              onRangeChange={handlePriceRangeChange}
            />
          </SearchFilterLayout>
        )}
        {showLookupNameFilter && (
          <SearchFilterLayout label="Name">
            <FeatureDiscoveryTooltip
              zIndex={MODAL_CONTENT + 1}
              content="Filter data by import name!"
              isOpen={shouldShowTooltip("filter-by-import-name")}
              onNext={nextFeature}
              isLast={isLastFeature}
            >
              <MultiSelectCombobox
                list={lookupsNameOptions}
                value={lookupIds}
                onRemove={handleRemoveLookupName}
                onSelect={handleSelectLookupName}
                placeholder="Select name(s)"
              />
            </FeatureDiscoveryTooltip>
          </SearchFilterLayout>
        )}
        {showLookupSourceFilter && (
          <SearchFilterLayout label="Source">
            <FeatureDiscoveryTooltip
              zIndex={MODAL_CONTENT + 1}
              content="Filter data by source!"
              isOpen={shouldShowTooltip("filter-by-import-source")}
              onNext={nextFeature}
              isLast={isLastFeature}
            >
              <MultiSelectCombobox
                list={lookupSourceOptions}
                value={lookupSources}
                onRemove={handleRemoveLookupSource}
                onSelect={handleSelectLookupSource}
                placeholder="Select source(s)"
              />
            </FeatureDiscoveryTooltip>
          </SearchFilterLayout>
        )}
        <div className="flex flex-row gap-4">
          <SearchFilterLayout label="Communication Type">
            <CommType
              value={commType}
              onChange={(newValue: string) => {
                setCommType(newValue);
              }}
            />
          </SearchFilterLayout>
          {showMlsFilter && (
            <SearchFilterLayout label="MLS">
              <Select
                value={mls === "" ? "all" : mls}
                onValueChange={(value) => {
                  setMls(value === "all" ? "" : value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select MLS" />
                </SelectTrigger>
                <SelectContent>
                  {mlsOptions.map((option) => (
                    <SelectItem key={option.id} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SearchFilterLayout>
          )}

          {showIncludeActiveOnMLS && (
            <SearchFilterLayout label="MLS Status">
              <Checkbox
                label="Show Actives"
                checked={showMLSActives}
                onCheckedChange={handleShowMLSActivesChange}
              />
            </SearchFilterLayout>
          )}
        </div>
        {showListingStatus && (
          <SearchFilterLayout label="MLS Status">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
              {statusList.map(({ name, value }) => {
                return (
                  <Checkbox
                    key={`status-checkbox-${value}`}
                    label={name}
                    checked={selectedListingStatuses.includes(value)}
                    onCheckedChange={(newIsChecked: boolean) => {
                      if (newIsChecked) {
                        if (!selectedListingStatuses.includes(value)) {
                          setSelectedListingStatuses([
                            ...selectedListingStatuses,
                            value,
                          ]);
                        }
                      } else {
                        setSelectedListingStatuses(
                          selectedListingStatuses.filter(
                            (status) => status !== value
                          )
                        );
                      }
                    }}
                  />
                );
              })}
            </div>
          </SearchFilterLayout>
        )}
        {showDistressedFilters && (
          <SearchFilterLayout label="Level Of Distress">
            <MultipleSelect
              headerItemFormatter={(item: string) =>
                item.replace(/^\d+\s-\s/, "")
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              list={distressedLevelOptions as any[]}
              value={selectedDistressedLevelOptions}
              onChange={handleDistressedLevelChange}
            />
          </SearchFilterLayout>
        )}

        {showPreForeclosureStatus && (
          <SearchFilterLayout label="Pre-Foreclosure Status">
            <MultipleSelect
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              list={preForeclosureStatusOptions as any[]}
              value={selectedPreForeclosureStatusOptions}
              onChange={handlePreForeclosureStatusChange}
            />
          </SearchFilterLayout>
        )}
        {showPropertyTypeFilter && setPropertyTypes && (
          <SearchFilterLayout label="Property Type">
            <PropertyTypeFilter
              selectedPropertyTypes={propertyTypes}
              onPropertyTypesChange={setPropertyTypes}
              options={propertyTypeOptions}
            />
          </SearchFilterLayout>
        )}
        <div className="flex gap-4 flex-grow flex-col sm:flex-row">
          <SearchFilterLayout label="Beds">
            <Select
              value={numBeds === "" ? "any" : numBeds}
              onValueChange={(value) => {
                setNumBeds(value === "any" ? "" : value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select beds" />
              </SelectTrigger>
              <SelectContent>
                {numBedsOptions.map((option) => (
                  <SelectItem key={option.id} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SearchFilterLayout>
          <SearchFilterLayout label="Bath">
            <Select
              value={numBaths === "" ? "any" : numBaths}
              onValueChange={(value) => {
                setNumBaths(value === "any" ? "" : value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select baths" />
              </SelectTrigger>
              <SelectContent>
                {numBathsOptions.map((option) => (
                  <SelectItem key={option.id} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SearchFilterLayout>
        </div>

        <SearchFilterLayout label="Square Feet">
          <Select
            value={sqFootage === "" ? "any" : sqFootage}
            onValueChange={(value) => {
              setSqFootage(value === "any" ? "" : value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select square feet" />
            </SelectTrigger>
            <SelectContent>
              {sqFootOptions.map((option) => (
                <SelectItem key={option.id} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SearchFilterLayout>

        <SearchFilterLayout label="Postal Code(s)">
          <TextInput
            placeholder="Ex. 84043"
            onChange={setPostalCode}
            value={postalCode}
            className="w-full"
            widthClassName="w-full"
          />
          <p className="text-[12px] leading-4 text-gray-secondary">
            Enter Postal codes separated by commas
          </p>
        </SearchFilterLayout>
        <SearchFilterLayout label="Tags">
          <TagSearch
            value={tags}
            onChange={setTags}
            isAndSearch={isTagsAndSearch}
            onSearchTypeChange={setIsTagsAndSearch}
            placeholder="Ex. tag"
          />
        </SearchFilterLayout>
        <SearchFilterLayout label="Phone Numbers">
          <Checkbox
            label="Show Do Not Call phone numbers"
            checked={!isHideDNCPhoneNumbers}
            onCheckedChange={handleHideDNCPhoneNumbersChange}
          />
        </SearchFilterLayout>
      </div>
    </div>
  );
};

export default SearchFilters;
